import React from 'react';
import * as R from 'ramda';
// components
import TextComponent from '../../components/text';
import { createMainCharge } from '../../components/charge/settings';
import { getRateFromSharedAccessAndFuelPrices } from '../../components/charge/hocs/helpers';
// features
import { cloRateInitFields } from '../rate/customer/settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// feature new-do
import {
  TAB_NAME_PRICING,
  PAGE_TYPE_CREATE_DO,
  PAGE_TYPE_CLO_TEMPLATE,
  PAGE_TYPE_LOAD_STOPS_EDIT,
  PAGE_TYPE_CREATE_FROM_EDI,
  TAB_NAME_RATE_CONFIRMATION,
  PAGE_TYPE_CREATE_FROM_QUOTE,
  PAGE_TYPE_CREATE_FROM_PUBLIC_CLO,
  PAGE_TYPE_CREATE_FROM_ORDER_QUOTE,
} from './constants';
import { locationFieldsToPick, stopInfoFieldsToPick } from './settings/fields-settings';
//////////////////////////////////////////////////

export const isPageCreateDO = (page) => R.equals(page, PAGE_TYPE_CREATE_DO);

export const isPageCreateFromEdi = (page) => R.equals(page, PAGE_TYPE_CREATE_FROM_EDI);

export const isPageCreateFromPublicClo = (page) => R.equals(page, PAGE_TYPE_CREATE_FROM_PUBLIC_CLO);

export const isPageCreateFromOrderQuote = (page) => R.equals(page, PAGE_TYPE_CREATE_FROM_ORDER_QUOTE);

export const isPageCreateFromQuote = (page) => R.equals(page, PAGE_TYPE_CREATE_FROM_QUOTE);

export const isPageCloTemplate = (page) => R.equals(page, PAGE_TYPE_CLO_TEMPLATE);

export const isPageLoadStopsEdit = (page) => R.equals(page, PAGE_TYPE_LOAD_STOPS_EDIT);

export const isSourceTypeEdi = (source) => R.equals(source, GC.CREATE_DO_SOURCE_TYPE_EDI);

export const isSourceTypeManual = (source) => R.equals(source, GC.CREATE_DO_SOURCE_TYPE_MANUAL);

export const isSourceTypeEdiOrManual = (source) => R.or(
  isSourceTypeEdi(source),
  isSourceTypeManual(source),
);

export const renderLeftRightEmptyInfo = (props) => {
  const { pageType, branchGuid } = props;
  if (isPageCreateFromQuote(pageType)) return false;
  if (G.isNilOrEmpty(branchGuid)) return true;
  return false;
};

export const renderLeftRightContent = (props) => {
  const { pageType, branchGuid } = props;
  if (isPageCreateFromQuote(pageType)) return true;
  if (G.isNotNilAndNotEmpty(branchGuid)) return true;
  return false;
};

const createPrimaryReferenceDisplayValue = (sequence, configPrefix, divisionPrefix) => {
  const arrayOfValues = [divisionPrefix, configPrefix, G.createSequenceApproxValue(sequence)];

  return G.createStringFromArray(arrayOfValues, '');
};

export const createCustomerLoadNumber = ({ configs, sequence, divisionPrefix }) => {
  const autogenerated = G.getConfigValueFromStore(
    GC.CLO_PRIMARY_REFERENCE_AUTOGENERATED, configs,
  );
  if (R.and(G.isTrue(autogenerated), G.isNotNil(sequence))) {
    const configPrefix = G.getConfigValueFromStore(
      GC.CLO_PRIMARY_REFERENCE_PREFIX, configs,
    );
    let divPref = divisionPrefix;
    if (G.isNilOrEmpty(divisionPrefix)) {
      divPref = G.getConfigValueFromStore(
        GC.CLO_PRIMARY_REFERENCE_DIVISION_PREFIX,
        configs,
      );
    }
    return createPrimaryReferenceDisplayValue(sequence, configPrefix, divPref);
  }
  return '';
};

export const getDataFromStops = (stops) => {
  const stopsArray = R.values(stops);
  const sortedStops = R.sortBy(R.prop(GC.FIELD_ORDER), stopsArray);
  const firstStop = R.head(sortedStops);
  const lastStop = R.last(sortedStops);
  const pickups = R.filter((stop) => R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE, stop), stopsArray);
  const drops = R.filter((stop) => R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE, stop), stopsArray);
  const pickedItems = R.reduce(
    (acc, pickup) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_LOAD_ITEMS], pickup)),
    [],
    pickups,
  );
  const droppedItems = R.reduce(
    (acc, drop) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_LOAD_ITEMS], drop)),
    [],
    drops,
  );
  const pickedItemsInternalIds = R.map((item) => R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), pickedItems);
  const droppedItemInternalIds = droppedItems;
  const pickedUpContainers = R.reduce(
    (acc, pickup) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_STOP_PICKED_UP_CONTAINERS], pickup)),
    [],
    stopsArray,
  );
  const droppedContainers = R.reduce(
    (acc, drop) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_STOP_DROPPED_CONTAINERS], drop)),
    [],
    stopsArray,
  );
  const pickedUpContainerIds = R.map((item) => R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item), pickedUpContainers);
  const droppedContainerIds = droppedContainers;
  const stopReferencesArr = R.reduce(
    (acc, stop) =>
      R.concat(acc, R.or(R.path(['formData', GC.FIELD_REFERENCES], stop), [])),
    [],
    stopsArray,
  );
  const stopReferences = R.assoc(GC.FIELD_REFERENCES, stopReferencesArr, {});

  return {
    drops,
    pickups,
    lastStop,
    firstStop,
    pickedItems,
    sortedStops,
    droppedItems,
    stopReferences,
    droppedContainers,
    pickedUpContainers,
    droppedContainerIds,
    pickedUpContainerIds,
    pickedItemsInternalIds,
    droppedItemInternalIds,
  };
};

export const getDataFromStopsTaken = (stops, order) => {
  const stopsArray = R.values(stops);
  const sortedStops = R.sortBy(R.prop(GC.FIELD_ORDER), stopsArray);
  const takenStops = R.take(order, sortedStops);
  const firstStop = R.head(sortedStops);
  const lastStop = R.last(sortedStops);
  const pickups = R.filter((stop) => R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE, stop), takenStops);
  const drops = R.filter((stop) => R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE, stop), takenStops);
  const pickedItems = R.reduce(
    (acc, pickup) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_LOAD_ITEMS], pickup)),
    [], pickups,
  );
  const droppedItems = R.reduce(
    (acc, drop) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_LOAD_ITEMS], drop)),
    [], drops,
  );
  const pickedItemsInternalIds = R.map((item) => R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), pickedItems);
  const droppedItemInternalIds = droppedItems;
  const pickedUpContainers = R.reduce(
    (acc, pickup) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_STOP_PICKED_UP_CONTAINERS], pickup)),
    [], takenStops,
  );
  const droppedContainers = R.reduce(
    (acc, drop) => R.concat(acc, R.pathOr([], ['formData', GC.FIELD_STOP_DROPPED_CONTAINERS], drop)),
    [], takenStops,
  );
  const pickedUpContainerIds = R.map((item) => R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item), pickedUpContainers);
  const droppedContainerIds = droppedContainers;

  return {
    drops,
    pickups,
    lastStop,
    firstStop,
    pickedItems,
    sortedStops,
    droppedItems,
    droppedContainers,
    pickedUpContainers,
    droppedContainerIds,
    pickedUpContainerIds,
    pickedItemsInternalIds,
    droppedItemInternalIds,
  };
};

export const isStopsQuantityLessOrEqualTwo = (stops) => R.compose(
  R.lte(R.__, 2),
  R.length(),
  R.values(),
)(stops);

const getItemWeightInfo = (item) => G.createStringFromArray(
  R.values(R.pick([GC.FIELD_ITEM_WEIGHT, GC.FIELD_ITEM_WEIGHT_TYPE], item)),
);

const getItemPackageInfo = (item) => G.createStringFromArray(
  R.values(R.pick([GC.FIELD_ITEM_QUANTITY, GC.FIELD_ITEM_PACKAGE_TYPE], item)),
);

const getItemPoNumberInfo = ({ poNumber }) => {
  if (G.isNilOrEmpty(poNumber)) return '';

  return `${G.getWindowLocale('titles:po#', 'PO#')}: ${poNumber}`;
};

const getItemLabel = (item) => {
  const id = G.ifElse(
    G.isNilOrEmpty(R.prop(GC.FIELD_ITEM_ID, item)),
    R.prop(GC.FIELD_ITEM_INTERNAL_ID, item),
    R.prop(GC.FIELD_ITEM_ID, item),
  );

  const infoArray = [getItemWeightInfo(item), getItemPackageInfo(item), getItemPoNumberInfo(item)];

  const label = `${id} (${G.createStringFromArray(infoArray, ', ')})`;

  return <TextComponent title={label} maxWidth='100%' withEllipsis={true}>{label}</TextComponent>;
};

export const getItemsForDropOptions = (stops, stopItems) => {
  const { pickedItems, droppedItemInternalIds } = getDataFromStops(stops);
  const itemsForDrop = R.reject(
    (item) => R.and(
      G.notContain(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), stopItems),
      R.includes(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), droppedItemInternalIds),
    ),
    pickedItems,
  );
  return R.compose(
    R.map((item) => (
      {
        label: getItemLabel(item),
        value: R.prop(GC.FIELD_ITEM_INTERNAL_ID, item),
      }
    )),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_ITEM_ID, item))),
  )(itemsForDrop);
};

const getContainerLabel = (item) => {
  const containerNumber = G.getPropFromObject(GC.FIELD_CONTAINER_NUMBER, item);
  const containerInitial = G.getPropFromObject(GC.FIELD_CONTAINER_INITIAL, item);
  const containerInternalId = G.getPropFromObject(GC.FIELD_CONTAINER_INTERNAL_ID, item);

  if (R.and(G.isNilOrEmpty(containerNumber), G.isNilOrEmpty(containerInitial))) return containerInternalId;

  return G.createStringFromArray([containerInitial, containerNumber], '');
};

export const getContainersForPickupItemOptions = (stops, stop) => {
  const { order } = stop;

  const { pickedUpContainers } = getDataFromStopsTaken(stops, order);

  return R.compose(
    R.map((item) => (
      {
        label: getContainerLabel(item),
        value: R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item),
      }
    )),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_CONTAINER_NUMBER, item))),
  )(pickedUpContainers);
};

export const getContainersForDropOptions = (stops, stop) => {
  const { order, formData } = stop;

  const { droppedContainerIds } = getDataFromStops(stops);
  const { pickedUpContainers } = getDataFromStopsTaken(stops, order);

  const stopPickedUpContainers = R.path([GC.FIELD_STOP_PICKED_UP_CONTAINERS], formData);
  const stopPickedUpContainerIds = R.compose(
    R.map((item) => R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item)),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item))),
  )(stopPickedUpContainers);
  const stopDroppedContainerIds = R.path([GC.FIELD_STOP_DROPPED_CONTAINERS], formData);

  return R.compose(
    R.map((item) => (
      {
        label: getContainerLabel(item),
        value: R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item),
      }
    )),
    R.reject((item) => R.and(
      G.notContain(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item), stopDroppedContainerIds),
      R.includes(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item), droppedContainerIds)),
    ),
    R.reject((item) => R.includes(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item), stopPickedUpContainerIds)),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_CONTAINER_NUMBER, item))),
  )(pickedUpContainers);
};

const dropItemsMapper = R.map((item) => item);
const pickupItemsMapper = R.map((item) => R.prop(GC.FIELD_ITEM_INTERNAL_ID, item));

export const getStopItemsInfo = (stop, stopsData) => {
  const { formData: { items }, eventType } = stop;
  const { pickedItems } = stopsData;
  const itemsMapper = G.ifElse(
    G.isEventTypePickup(eventType),
    pickupItemsMapper,
    dropItemsMapper,
  );
  const mappedStopItems = itemsMapper(R.or(items, []));
  const indexedPickedItems = R.compose(
    R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item))),
    R.reject((item) => G.isNilOrEmpty(R.prop(GC.FIELD_ITEM_ID, item))),
  )(pickedItems);
  const itemsToUse = R.values(R.pick(mappedStopItems, indexedPickedItems));
  return G.getStopItemsInfo(itemsToUse);
};

export const getStopTitle = (stop, formValues) => {
  const { order, eventType } = stop;
  const stopTypeLocaleMap = {
    [GC.EVENT_TYPE_DROP]: 'titles:drop',
    [GC.EVENT_TYPE_PICKUP]: 'titles:pickup',
  };
  const address = G.concatLocationFields(formValues);
  return `
    ${G.getWindowLocale(stopTypeLocaleMap[eventType], eventType)}:
    ${G.getWindowLocale('titles:stop', 'stop')} ${order}.
    ${address}
  `;
};

export const getStopTitleFromStop = (stop) => {
  const { order, location, eventType } = stop;
  const stopTypeLocaleMap = {
    [GC.EVENT_TYPE_DROP]: 'titles:drop',
    [GC.EVENT_TYPE_PICKUP]: 'titles:pickup',
  };
  const address = G.concatLocationFields(location);
  return `
    ${G.getWindowLocale(stopTypeLocaleMap[eventType], eventType)}:
    ${G.getWindowLocale('titles:stop', 'stop')} ${order}.
    ${address}
  `;
};

export const getStopTitleFromFormData = (stop) => {
  const { order, formData, eventType } = stop;

  const stopTypeLocaleMap = {
    [GC.EVENT_TYPE_DROP]: 'titles:drop',
    [GC.EVENT_TYPE_PICKUP]: 'titles:pickup',
  };

  const address = G.concatLocationFields(
    formData,
    [GC.FIELD_LOCATION_NAME, GC.FIELD_CITY, GC.FIELD_STATE, GC.FIELD_ZIP, GC.FIELD_COUNTRY],
  );

  return `
    ${G.getWindowLocale(stopTypeLocaleMap[eventType], eventType)}:
    ${G.getWindowLocale('titles:stop', 'stop')} ${order}.
    ${address}
  `;
};

export const getStopIconColor = (isValid) => G.getTheme(G.ifElse(
  G.isTrue(isValid),
  'createDO.loadStepHeaderLeftIcon.svgsSuccessColor',
  'createDO.loadStepHeaderLeftIcon.svgsColor',
));

export const getStopLocationIcon = (stop) => {
  const { isValid, eventType } = stop;
  const color = getStopIconColor(isValid);
  if (R.equals(eventType, GC.EVENT_TYPE_PICKUP)) {
    return I.pickupStopIcon(null, null, null, color);
  } else if (R.equals(eventType, GC.EVENT_TYPE_DROP)) {
    return I.dropStopIcon(null, null, null, color);
  }
};

export const getStopLocationIconSimple = (stop) => {
  const { eventType } = stop;
  const color = G.getTheme('createDO.loadStepHeaderLeftIcon.svgsSuccessColor');
  if (R.equals(eventType, GC.EVENT_TYPE_PICKUP)) {
    return I.pickupStopIcon(null, null, null, color);
  } else if (R.equals(eventType, GC.EVENT_TYPE_DROP)) {
    return I.dropStopIcon(null, null, null, color);
  }
};

export const getStopDistanceInfo = (manual, distance) => {
  if (G.isNotNil(manual)) {
    return `
      ${R.pathOr('', [GC.FIELD_DISTANCE_MANUAL], distance)}
      ${R.pathOr('', [GC.FIELD_DISTANCE_MANUAL_UOM], distance)}
    `;
  }
  return `
    ${R.pathOr('', [GC.FIELD_DISTANCE_SYSTEM], distance)}
    ${R.pathOr('', [GC.FIELD_DISTANCE_SYSTEM_UOM], distance)}
  `;
};

export const getStopHeaderActionOptions = (props) => {
  const { stopOrder, handleCleanForm, removeStopFromStore, handleSearchLocation } = props;

  const actions = [
    {
      Icon: I.globalSearch2,
      action: handleSearchLocation,
      title: G.getWindowLocale('titles:search-location', 'Search Location'),
    },
    {
      Icon: I.clean,
      action: () => handleCleanForm(stopOrder),
      title: G.getWindowLocale('titles:clean-form', 'Clean Form'),
    },
  ];

  if (G.isFunction(removeStopFromStore)) {
    return R.append({
      Icon: I.trash,
      action: () => removeStopFromStore(stopOrder),
      title: G.getWindowLocale('titles:remove-stop', 'Remove Stop'),
    }, actions);
  }

  return actions;
};

export const getBillToHeaderActionOptions = (props) => ([
  {
    Icon: I.globalSearch2,
    action: props.handleSearchLocation,
    title: G.getWindowLocale('titles:search-location', 'Search Location'),
  },
  {
    Icon: I.clean,
    action: props.handleCleanForm,
    title: G.getWindowLocale('titles:clean-form', 'Clean Form'),
  },
]);

const keysMap = {
  distance: GC.FIELD_DISTANCE_SYSTEM,
  unit: GC.FIELD_DISTANCE_SYSTEM_UOM,
};

const emptyDistance = {
  [GC.FIELD_DISTANCE_SYSTEM]: null,
  [GC.FIELD_DISTANCE_SYSTEM_UOM]: null,
};

export const mapStopsWithDistances = (loadType, sortedStops, distanceRes) => {
  const distancesArr = R.compose(
    R.append(emptyDistance),
    R.map((item) => G.renameKeys(keysMap, item)),
    R.map((item) => R.pick(['distance', 'unit'], item)),
    R.prop('stopResults'),
  )(distanceRes);

  const zippedStops = R.zipWith((stop, distance) => {
    const distanceToNextStop = R.prop(G.getDistancePropByLoadType(loadType), stop);
    const newDistanceToNextStop = R.mergeRight(distanceToNextStop, distance);

    return R.assoc(G.getDistancePropByLoadType(loadType), newDistanceToNextStop, stop);
  }, sortedStops, distancesArr);

  return R.indexBy(R.prop(GC.FIELD_ORDER), zippedStops);
};

export const getLoadStopsGeodata = R.reduce((acc, stop) => {
  if (R.isNil(acc)) return null;

  const latitude = R.pathOr(null, ['formData', GC.FIELD_LATITUDE], stop);
  const longitude = R.pathOr(null, ['formData', GC.FIELD_LONGITUDE], stop);
  const city = R.pathOr(null, ['formData', GC.FIELD_CITY], stop);
  const state = R.pathOr(null, ['formData', GC.FIELD_STATE], stop);
  const zip = R.pathOr(null, ['formData', GC.FIELD_ZIP], stop);

  const cityState = { city, state };

  if (R.and(G.isNotNil(latitude), G.isNotNil(longitude))) {
    return R.append({ ...cityState, zip, latitude, longitude }, acc);
  }

  if (R.or(R.and(G.isNotNil(city), G.isNotNil(state)), G.isNotNil(zip))) {
    return R.append({ ...cityState, zip }, acc);
  }

  return null;
}, []);

export const getStopPointsLatLonStringsArray = R.map((stop) => {
  const latitude = G.getPropFromObject(GC.FIELD_LATITUDE, stop);
  const longitude = G.getPropFromObject(GC.FIELD_LONGITUDE, stop);

  if (R.or(G.isNilOrEmpty(latitude), G.isNilOrEmpty(longitude))) return null;

  return `${latitude},${longitude}`;
});

export const reorderStops = (sortedStops, sourceIndex, destinationIndex) => {
  const stops = R.clone(sortedStops);
  const [removedStop] = stops.splice(sourceIndex, 1);

  stops.splice(destinationIndex, 0, removedStop);

  return R.compose(
    R.indexBy(R.prop(GC.FIELD_ORDER)),
    G.mapIndexed((stop, index) => R.assoc(GC.FIELD_ORDER, R.inc(index), stop)),
  )(stops);
};

export const mapStopOrderFieldToProp = (loadType, stops) => R.map(
  (item) => R.assoc(
    G.getEventIndexPropByLoadType(loadType),
    R.path([GC.FIELD_ORDER], item),
    item,
  ),
  stops,
);

export const makeReqBillTo = (billTo) => {
  const locationWithTemplId = G.resetTemplateIdToLocation(billTo);

  return G.resetLocationTypeToLocation(locationWithTemplId);
};

export const makeLocation = (location) => {
  const contacts = R.compose(
    R.filter(R.compose(
      G.isOneNotNilOrNotEmpty,
      R.values,
      R.pick([GC.FIELD_FAX, GC.FIELD_PHONE, GC.FIELD_EMAIL, GC.FIELD_CONTACT_NAME, GC.FIELD_PHONE_EXTENSION]),
    )),
    R.pathOr([], [GC.FIELD_CONTACTS]),
  )(location);

  const locationWithValidContacts = R.assoc(GC.FIELD_CONTACTS, contacts, location);

  return G.resetLocationTypeToLocation(G.resetTemplateIdToLocation(locationWithValidContacts));
};

export const makeLocationForTemplate = (location, branchConfigs) => {
  const validLocation = makeLocation(location);

  const options = G.getFullOptionsFromDropdownConfigStore(branchConfigs, GC.TEMPLATES_LOCATION_TYPE);

  const indexedOptions = G.indexMappedDropdownOptionsByField(
    GC.FIELD_DROPDOWN_OPTION_GUID,
    G.mapDropdownConfigOptionsToObjects,
    options,
  );

  return R.assoc(
    GC.FIELD_LOCATION_TYPE,
    G.getPropFromObject(G.getPropFromObject(GC.FIELD_LOCATION_TYPE, validLocation), indexedOptions),
    validLocation,
  );
};

const makeItemIds = (eventType, items) => {
  if (G.isEventTypePickup(eventType)) {
    return R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), items);
  }
  return items;
};

const makeContainerIds = (formData, pickedUpContainers = []) => {
  let droppedContainerIds = R.reject(G.isNilOrEmpty, R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, formData));

  if (G.isFirstItemObject(droppedContainerIds)) {
    droppedContainerIds = R.map(
      R.prop(GC.FIELD_CONTAINER_INTERNAL_ID),
      droppedContainerIds,
    );
  }

  const orderPickedUpContainers = R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers);
  droppedContainerIds = R.filter(
    (id) => G.isNotNilAndNotEmpty(R.prop(id, orderPickedUpContainers)),
  )(droppedContainerIds);
  const pickedUpContainerIds = R.map(
    R.prop(GC.FIELD_CONTAINER_INTERNAL_ID),
    R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS, formData),
  );

  return { droppedContainerIds, pickedUpContainerIds };
};

const makeDroppedContainers = (formData, pickedContainers) => {
  const formDataDroppedContainers = R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, formData);

  const indexed = R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedContainers);

  let containers = [];

  if (G.isFirstItemObject(formDataDroppedContainers)) {
    containers = R.map(
      ({ containerInternalId }) => R.prop(containerInternalId, indexed),
      formDataDroppedContainers,
    );
  } else {
    containers = R.map(
      (id) => R.prop(id, indexed),
      formDataDroppedContainers,
    );
  }

  return R.filter(G.isNotNil, containers);
};

const makeRequestContainers = (formData, picked, isEdit = false, loadInfo = {}) => {
  const formDataPickedUpContainers = R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS, formData);
  let formDataDroppedContainers = R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, formData);

  if (G.isFirstItemObject(formDataDroppedContainers)) {
    formDataDroppedContainers = R.map(
      R.prop(GC.FIELD_CONTAINER_INTERNAL_ID),
      formDataDroppedContainers,
    );
  }

  const omitArray = G.ifElse(isEdit, [], GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR);

  const pickedUpContainers = R.map(
    (container) => R.mergeRight(G.mapObjectEmptyStringFieldsToNull(R.omit(omitArray, container)), loadInfo),
    formDataPickedUpContainers,
  );

  const droppedContainers = R.compose(
    R.map((container) => R.mergeRight(container, loadInfo)),
    R.filter((item) => R.includes(
      R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, item),
      formDataDroppedContainers,
    )),
    R.map(R.omit(omitArray)),
  )(picked);

  return { droppedContainers, pickedUpContainers };
};

export const mapReferencesWithRefTypes = (references, branchRefTypes) => {
  if (R.or(G.isNilOrEmpty(references), G.isNilOrEmpty(branchRefTypes))) return [];

  const indexedTypes = R.indexBy(R.prop(GC.FIELD_GUID), R.prop(GC.LOAD_TYPE_CLO, branchRefTypes));

  return R.compose(
    R.filter(({ value, referenceTypeGuid }) => R.and(
      G.isNotNilAndNotEmpty(value),
      G.isNotNilAndNotEmpty(referenceTypeGuid),
    )),
    R.map((item) => R.assoc(
      GC.FIELD_NAME,
      R.path([R.prop(GC.FIELD_REFERENCE_TYPE_GUID, item), GC.FIELD_NAME], indexedTypes),
      item,
    )),
  )(references);
};

export const makeReqReferences = (store) => {
  const {
    branchConfigs,
    divisionPrefix,
    branchRefTypes,
    primaryReference,
    referenceFormData,
    primaryRefSequence,
    customerLoadNumber,
    customerReferenceValue,
  } = store;

  const references = mapReferencesWithRefTypes(R.prop(GC.FIELD_LOAD_REFERENCES, referenceFormData), branchRefTypes);

  const mappedRefs = R.map(({ isAdditional, originalValue, ...item }) => {
    const { value } = item;

    if (isAdditional) {
      return {
        ...item,
        value: G.ifElse(R.equals(value, originalValue), null, value),
      };
    }

    return item;
  }, references);

  const primaryRefSequenceValue = createCustomerLoadNumber({
    divisionPrefix,
    configs: branchConfigs,
    sequence: primaryRefSequence,
  });

  const primaryValue = G.ifElse(
    G.notEquals(customerLoadNumber, primaryRefSequenceValue),
    customerLoadNumber,
    null,
  );

  const primaryReferenceTypeGuidConfig = G.getConfigValueFromStore(
    GC.CLO_PRIMARY_REFERENCE_TYPE,
    branchConfigs,
  );

  const primaryReferenceTypeGuid = G.ifElse(
    G.isNotNilAndNotEmpty(primaryReferenceTypeGuidConfig),
    primaryReferenceTypeGuidConfig,
    R.path([GC.FIELD_VALUE], primaryReference),
  );

  const primary = {
    primary: true,
    value: primaryValue,
    [GC.FIELD_REFERENCE_TYPE_GUID]: G.ifElse(
      G.isNotNilAndNotEmpty(primaryRefSequenceValue),
      primaryReferenceTypeGuid,
      R.propOr(null, GC.FIELD_VALUE, primaryReference),
    ),
  };

  if (G.isNotNilAndNotEmpty(customerReferenceValue)) {
    const customerRefTypeGuid = G.getConfigValueFromStore(
      GC.CLO_GENERAL_CUSTOMER_REFERENCE_TYPE,
      branchConfigs,
    );

    const customerRef = {
      value: customerReferenceValue,
      [GC.FIELD_REFERENCE_TYPE_GUID]: customerRefTypeGuid,
    };

    return [
      ...mappedRefs,
      primary,
      customerRef,
    ];
  }

  return R.append(primary, mappedRefs);
};

const getMainStopFields = (stop) => {
  const fields = R.pick(stopInfoFieldsToPick, stop);
  const apptEarly = R.path([GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], fields);
  const apptLate = R.path([GC.FIELD_LOAD_APPOINTMENT_LATE_TIME], fields);
  const apptLateToUse = G.ifElse(
    G.isNilOrEmpty(apptLate),
    apptEarly,
    apptLate,
  );
  const fieldsToUse = R.assoc(GC.FIELD_LOAD_APPOINTMENT_LATE_TIME, apptLateToUse, fields);

  return G.mapObjectEmptyStringFieldsToNull(fieldsToUse);
};

export const getStopTrailerObjectFields = (values, trailers) => {
  const getTrailersByGuids = (fieldName) => {
    const trailerGuids = R.propOr([], fieldName, values);

    return R.filter(
      (trailer) => R.includes(G.getGuidFromObject(trailer), trailerGuids),
      R.or(trailers, []),
    );
  };

  return {
    [GC.FIELD_STOP_DROPPED_TRAILERS]: getTrailersByGuids(GC.FIELD_STOP_DROPPED_TRAILER_GUIDS),
    [GC.FIELD_STOP_PICKED_UP_TRAILERS]: getTrailersByGuids(GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS),
  };
};

export const makeSingleTelStop = (stopValues, primaryObjectGuid) => {
  const stop = G.formatStopDateTimeValues(G.mapObjectEmptyStringFieldsToNull(stopValues));
  const stopFields = getMainStopFields(stop);
  const locationFields = makeLocation(R.pick(locationFieldsToPick, stop));
  const location = R.mergeRight(stop.location, locationFields);
  const fcfs = R.prop(GC.FIELD_LOAD_FCFS, stopFields);
  const eventLateDate = R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, stopFields);
  const eventLateDateToUse = G.ifElse(G.isFalse(fcfs), null, eventLateDate);

  const stopInfo = R.assoc(
    GC.FIELD_LOAD_EVENT_LATE_DATE,
    eventLateDateToUse,
    stopFields,
  );

  const references = R.compose(
    R.map((ref) => {
      if (R.isNil(G.getGuidFromObject(ref))) {
        const primaryObjectGuidToUse = G.ifElse(
          G.isLoadTypeClo(stop),
          R.prop(GC.FIELD_CLO_GUID, stop),
          primaryObjectGuid,
        );

        return R.assoc(GC.FIELD_PRIMARY_OBJECT_GUID, primaryObjectGuidToUse, ref);
      }

      return ref;
    }),
    R.prop(GC.FIELD_REFERENCES),
  )(stop);

  const data = {
    ...stop,
    location,
    references,
    ...stopInfo,
  };

  return R.omit(locationFieldsToPick, data);
};

// TODO: check with containers logic in load planner and route builder
export const makeStopsForSaveAndUpdate = (
  stops,
  branchRefTypes,
  pickedItems,
  prevEvents,
  pickedUpContainers,
) => R.map(
  (item) => {
    const { order, eventType, formData, cloDistanceToNextStop } = item;

    const eventGuid = R.path([GC.FIELD_GUID], formData);
    const itemIds = makeItemIds(eventType, R.prop(GC.FIELD_LOAD_ITEMS, formData));
    const stopFields = getMainStopFields(formData);

    let location = makeLocation(R.pick(locationFieldsToPick, formData));

    if (G.isNotNil(eventGuid)) {
      location = R.mergeRight(location, R.pick(
        [GC.FIELD_GUID, GC.FIELD_VERSION],
        R.path([eventGuid, GC.FIELD_LOCATION], prevEvents),
      ));
    }

    if (R.pathEq(
      R.prop(GC.FIELD_LOCATION_TYPE, formData),
      [eventGuid, GC.FIELD_LOCATION, GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID],
      prevEvents,
    )) {
      location = R.assoc(
        GC.FIELD_LOCATION_TYPE,
        R.path([eventGuid, GC.FIELD_LOCATION, GC.FIELD_LOCATION_TYPE], prevEvents),
        location,
      );
    }

    const references = mapReferencesWithRefTypes(R.prop(GC.FIELD_LOAD_REFERENCES, formData), branchRefTypes);

    const containerIds = makeContainerIds(formData, pickedUpContainers);
    const droppedContainers = makeDroppedContainers(formData, pickedUpContainers);

    const pickedUpContainersToSave = R.compose(
      R.map((id) => R.find(R.propEq(id, GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers)),
      R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS),
    )(containerIds);

    let stop = G.formatStopDateTimeValues({
      itemIds,
      location,
      ...stopFields,
      ...containerIds,
      droppedContainers,
      [GC.FIELD_EVENT_TYPE]: eventType,
      [GC.FIELD_CLO_EVENT_INDEX]: order,
      [GC.FIELD_LOAD_REFERENCES]: references,
      [GC.FIELD_DISTANCE_CLO]: cloDistanceToNextStop,
      [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: pickedUpContainersToSave,
    });

    if (G.isNotNil(eventGuid)) {
      stop = R.mergeRight(stop, R.pick([GC.FIELD_GUID, GC.FIELD_VERSION], R.prop(eventGuid, prevEvents)));
    }

    if (G.isEventTypePickup(eventType)) {
      return R.assoc(GC.FIELD_LOAD_ITEMS, R.prop(GC.FIELD_LOAD_ITEMS, formData), stop);
    }

    if (G.isEventTypeDrop(eventType)) {
      const items = R.filter(
        (item) => R.includes(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), stop.itemIds),
        pickedItems,
      );

      return R.assoc(GC.FIELD_LOAD_ITEMS, items, stop);
    }

    return stop;
  },
  R.values(stops),
);

export const makeReqStops = (
  stops,
  branchRefTypes,
  pickedItems,
  pickedUpContainers,
  forIntegration = false,
) => R.map(
  (item) => {
    const { order, eventType, formData, cloDistanceToNextStop } = item;

    const formDataToUse = G.formatStopDateTimeValues(G.mapObjectEmptyStringFieldsToNull(formData));

    const { dropTrailer, droppedTrailerGuids, pickedUpTrailerGuids } = formDataToUse;

    const internalId = G.getPropFromObject(GC.FIELD_INTERNAL_ID, formData);

    const itemIds = makeItemIds(eventType, R.prop(GC.FIELD_LOAD_ITEMS, formData));

    const containerIds = makeContainerIds(formData, pickedUpContainers);

    const stopFields = getMainStopFields(formDataToUse);

    const location = makeLocation(R.pick(locationFieldsToPick, formDataToUse));

    const references = mapReferencesWithRefTypes(R.prop(GC.FIELD_LOAD_REFERENCES, formData), branchRefTypes);

    const fcfs = R.prop(GC.FIELD_LOAD_FCFS, stopFields);
    const eventLateDate = R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, stopFields);

    const eventLateDateToUse = G.ifElse(
      G.isFalse(fcfs),
      null,
      eventLateDate,
    );

    const stopInfo = R.assoc(GC.FIELD_LOAD_EVENT_LATE_DATE, eventLateDateToUse, stopFields);

    let stop = {
      itemIds,
      location,
      internalId,
      ...stopInfo,
      ...containerIds,
      droppedTrailerGuids,
      pickedUpTrailerGuids,
      [GC.FIELD_EVENT_TYPE]: eventType,
      [GC.FIELD_CLO_EVENT_INDEX]: order,
      [GC.FIELD_LOAD_REFERENCES]: references,
      [GC.FIELD_DISTANCE_CLO]: cloDistanceToNextStop,
    };

    if (forIntegration) {
      let items = R.pathOr([], [GC.FIELD_LOAD_ITEMS], formData);

      if (G.isStopDrop(item)) {
        items = R.filter(({ itemInternalId }) => R.includes(itemInternalId, items), R.or(pickedItems, []));
      }

      const containers = makeRequestContainers(formData, pickedUpContainers);

      stop = { ...stop, items, ...containers };
    }

    return stop;
  },
  R.values(stops),
);

export const makeReqStopsForTemplate = ({
  stops,
  pickedItems,
  branchConfigs,
  branchRefTypes,
  pickedUpContainers,
}) => R.map(
  (item) => {
    const { order, eventType, formData, cloDistanceToNextStop } = item;

    const formDataToUse = G.formatStopDateTimeValues(G.mapObjectEmptyStringFieldsToNull(formData));

    const { droppedTrailerGuids, pickedUpTrailerGuids } = formDataToUse;

    const internalId = G.getPropFromObject(GC.FIELD_INTERNAL_ID, formData);

    const itemIds = makeItemIds(eventType, R.prop(GC.FIELD_LOAD_ITEMS, formData));

    const containerIds = makeContainerIds(formData, pickedUpContainers);

    const references = mapReferencesWithRefTypes(R.prop(GC.FIELD_LOAD_REFERENCES, formData), branchRefTypes);

    const stopFields = getMainStopFields(formDataToUse);

    const fcfs = R.prop(GC.FIELD_LOAD_FCFS, stopFields);
    const eventLateDate = R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, stopFields);

    const location = makeLocationForTemplate(R.pick(locationFieldsToPick, formDataToUse), branchConfigs);

    const eventLateDateToUse = G.ifElse(
      G.isFalse(fcfs),
      null,
      eventLateDate,
    );

    const stopInfo = R.assoc(GC.FIELD_LOAD_EVENT_LATE_DATE, eventLateDateToUse, stopFields);

    const stop = {
      itemIds,
      location,
      internalId,
      ...stopInfo,
      ...containerIds,
      droppedTrailerGuids,
      pickedUpTrailerGuids,
      [GC.FIELD_EVENT_TYPE]: eventType,
      [GC.FIELD_CLO_EVENT_INDEX]: order,
      [GC.FIELD_LOAD_REFERENCES]: references,
      [GC.FIELD_DISTANCE_CLO]: cloDistanceToNextStop,
    };

    if (G.isEventTypePickup(eventType)) {
      const items = R.map(
        R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR),
        R.prop(GC.FIELD_LOAD_ITEMS, formData),
      );
      const containers = makeRequestContainers(formData, pickedUpContainers);

      return { ...stop, items, ...containers };
    }

    if (G.isEventTypeDrop(eventType)) {
      const items = R.compose(
        R.filter((item) => R.includes(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), stop.itemIds)),
        R.map(R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR)),
      )(pickedItems);
      const containers = makeRequestContainers(formData, pickedUpContainers);

      return { ...stop, items, ...containers };
    }

    return stop;
  },
  R.values(stops),
);


export const makeLoadStopsEditReqStops = ({
  stops,
  loadGuid,
  loadType,
  pickedItems,
  branchRefTypes,
  pickedUpContainers,
}) => R.map(
  (item) => {
    const { order, eventType, formData, originEvent } = item;

    const { droppedTrailerGuids, pickedUpTrailerGuids } = formData;

    const orderProp = G.getEventIndexPropByLoadType(loadType);
    const distanceProp = G.getDistancePropByLoadType(loadType);
    const loadDistance = R.path([distanceProp], item);
    const itemIds = makeItemIds(eventType, R.prop(GC.FIELD_LOAD_ITEMS, formData));
    const containerIds = makeContainerIds(formData, pickedUpContainers);
    const stopFields = getMainStopFields(formData);

    let location = makeLocation(R.pick(locationFieldsToPick, formData));

    const references = mapReferencesWithRefTypes(R.prop(GC.FIELD_LOAD_REFERENCES, formData), branchRefTypes);

    const fcfs = R.prop(GC.FIELD_LOAD_FCFS, stopFields);
    const eventLateDate = R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, stopFields);

    const eventLateDateToUse = G.ifElse(
      G.isFalse(fcfs),
      null,
      eventLateDate,
    );

    const stopInfo = R.assoc(GC.FIELD_LOAD_EVENT_LATE_DATE, eventLateDateToUse, stopFields);

    if (G.isNotNilAndNotEmpty(originEvent)) {
      location = R.mergeRight(location, R.pick(
        [GC.FIELD_GUID, GC.FIELD_VERSION],
        R.path([GC.FIELD_LOCATION], originEvent),
      ));
    }

    const stop = G.formatStopDateTimeValues({
      ...originEvent,
      itemIds,
      location,
      ...stopInfo,
      ...containerIds,
      [orderProp]: order,
      droppedTrailerGuids,
      pickedUpTrailerGuids,
      [distanceProp]: loadDistance,
      [GC.FIELD_EVENT_TYPE]: eventType,
      [GC.FIELD_LOAD_REFERENCES]: references,
    });

    if (G.isEventTypePickup(eventType)) {
      const items = R.map(
        (item) => ({...item, loadGuid, loadType}),
        R.prop(GC.FIELD_LOAD_ITEMS, formData),
      );

      const containers = makeRequestContainers(formData, pickedUpContainers, true, { loadGuid, loadType });

      return { ...stop, items, ...containers };
    }

    if (G.isEventTypeDrop(eventType)) {
      const items = R.compose(
        R.filter((item) => R.includes(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), stop.itemIds)),
        R.map((item) => ({...item, loadGuid, loadType})),
      )(pickedItems);

      const containers = makeRequestContainers(formData, pickedUpContainers, true, { loadGuid, loadType });

      return { ...stop, items, ...containers };
    }

    return stop;
  },
  R.values(stops),
);

export const getMappedEquipmentFromConfigsByOptionGuid = (optionGuid, configs) => {
  if (G.isNilOrEmpty(optionGuid)) return null;

  const options = G.getFullOptionsFromDropdownConfigStore(configs, GC.GENERAL_EQUIPMENTS);
  const mappedOptions = G.mapDropdownConfigOptionsToObjects(options);

  return R.find(R.propEq(optionGuid, GC.FIELD_DROPDOWN_OPTION_GUID), mappedOptions);
};

export const getDisplayedValueFromDropdownConfig = (props) => {
  const { guid, branchConfigs, dropdownName } = props;

  if (G.isNilOrEmpty(guid)) return null;

  return R.compose(
    R.prop(GC.FIELD_DISPLAYED_VALUE),
    R.find(R.propEq(guid, GC.FIELD_GUID)),
    R.pathOr([], [dropdownName, 'options']),
  )(branchConfigs);
};

export const getMappedServicesFromConfigsByOptionGuid = (optionGuids = [], configs) => {
  const options = G.getFullOptionsFromDropdownConfigStore(configs, GC.GENERAL_SERVICES);

  const indexedOptions = G.indexMappedDropdownOptionsByField(
    GC.FIELD_DROPDOWN_OPTION_GUID,
    G.mapDropdownConfigOptionsToObjects,
    options,
  );

  return R.values(R.pick(R.or(optionGuids, []), indexedOptions));
};

export const makeReqEquipmentAndServices = (branchConfigs, referenceFormData) => {
  const equipmentGuid = R.prop(GC.FIELD_LOAD_EQUIPMENT, referenceFormData);
  const servicesGuids = R.prop(GC.FIELD_LOAD_SERVICES, referenceFormData);
  const equipment = getMappedEquipmentFromConfigsByOptionGuid(equipmentGuid, branchConfigs);
  const services = getMappedServicesFromConfigsByOptionGuid(servicesGuids, branchConfigs);

  return { services, equipment };
};

const getInitialValuesWithWeight = (initialValues, totalWeight) => {
  const { totalTripWeight, totalTripWeightUom } = initialValues;

  const totalTripWeightToUse = R.or(totalTripWeight, R.path([GC.FIELD_TOTAL_TRIP_WEIGHT], totalWeight));
  const totalTripWeightUomToUse = R.or(totalTripWeightUom, R.path([GC.FIELD_TOTAL_TRIP_WEIGHT_UOM], totalWeight));

  const totalWeightObject = {
    [GC.FIELD_TOTAL_TRIP_WEIGHT]: totalTripWeightToUse,
    [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: totalTripWeightUomToUse,
  };

  return R.mergeRight(initialValues, totalWeightObject);
};

const getCloRateInitFields = (props) => {
  const {
    isEditMode,
    branchConfigs,
    defaultOrderAccessorials,
    sharedAccessorialsApplyTo,
    defaultOrderFuelAccessorial,
  } = props;

  if (G.isTrue(isEditMode)) return cloRateInitFields;

  const mode = G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_TRANSPORTATION_MODE, branchConfigs);

  const sharedApplyToFiltered = G.filterChargesByModeOrNoMode(R.or(sharedAccessorialsApplyTo, []), mode);

  const allAccessorials = R.concat(R.or(defaultOrderAccessorials, []), sharedApplyToFiltered);
  const addMXLineHaul = G.getConfigValueFromStore(GC.CLO_RATE_ADD_MX_LINE_HAUL, branchConfigs);

  let charges = G.addMainFuelChargeToCharges2(allAccessorials, defaultOrderFuelAccessorial);

  if (addMXLineHaul) {
    charges = R.append(
      R.assoc(
        GC.FIELD_DISPLAYED_VALUE,
        G.getWindowLocale('titles:mx-line-haul', 'MX Line Haul'),
        createMainCharge(),
      ),
      charges,
    );
  }

  const cloRateInitFieldsWithCharges = R.assoc(GC.FIELD_CHARGES, charges, cloRateInitFields);

  return cloRateInitFieldsWithCharges;
};

export const getServiceGuidsFromStopsFormData = (stops) => R.compose(
  R.uniq,
  R.flatten,
  R.map(R.pathOr([], ['formData', GC.FIELD_SERVICE_GUIDS])),
  R.values,
)(stops);

export const filterServicesByMode = ({
  initialValues,
  branchConfigs,
  currentServices,
  transportationModeGroupingList,
}) => {
  const mode = R.pathOr(
    G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_TRANSPORTATION_MODE, branchConfigs),
    [GC.FIELD_MODE],
    initialValues,
  );

  const availableServices = R.pathOr([], [mode, GC.FIELD_SERVICES], transportationModeGroupingList);

  if (R.or(R.isEmpty(availableServices), G.isNilOrEmpty(currentServices))) return currentServices;

  const filtered = R.filter((item) => R.includes(item, availableServices), currentServices);

  return G.ifElse(R.isEmpty(filtered), null, filtered);
};

export const isStopsServicesChanged = (stopsServiceGuids, prevStopsServiceGuids) => R.and(
  G.isNotNil(prevStopsServiceGuids),
  G.notEquals(stopsServiceGuids, prevStopsServiceGuids),
);

export const getCustomerRateInitialServices = (props) => {
  const {
    stops,
    services,
    initialValues,
    branchConfigs,
    prevStopsServiceGuids,
    transportationModeGroupingList,
  } = props;

  const stopsServiceGuids = getServiceGuidsFromStopsFormData(stops);

  if (G.isNotNilAndNotEmpty(initialValues)) {
    if (isStopsServicesChanged(stopsServiceGuids, prevStopsServiceGuids)) {
      return filterServicesByMode({
        initialValues,
        branchConfigs,
        transportationModeGroupingList,
        currentServices: stopsServiceGuids,
      });
    }

    return R.prop(GC.FIELD_SERVICES, initialValues);
  }

  if (G.isNilOrEmpty(stopsServiceGuids)) return services;

  return filterServicesByMode({
    initialValues,
    branchConfigs,
    transportationModeGroupingList,
    currentServices: R.uniq(R.concat(stopsServiceGuids, R.or(services, []))),
  });
};

export const setDefaultCustomerRateValues = (props) => {
  const {
    equipment,
    totalWeight,
    branchConfigs,
    totalDistance,
    initialValues,
    asyncTotalDistance,
    totalPickupQuantity,
  } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const initValues = G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE],
      initialValues,
    );

    const totalPickupQuantityToUse = {
      [GC.FIELD_PACKAGE_TYPE]: R.or(
        R.prop(GC.FIELD_PACKAGE_TYPE, initialValues),
        R.prop(GC.FIELD_PACKAGE_TYPE, totalPickupQuantity),
      ),
      [GC.FIELD_TOTAL_PICKUP_QUANTITY]: R.or(
        R.prop(GC.FIELD_TOTAL_PICKUP_QUANTITY, initialValues),
        R.prop(GC.FIELD_TOTAL_PICKUP_QUANTITY, totalPickupQuantity),
      ),
    };

    const initValuesWithWeight = getInitialValuesWithWeight(initValues, totalWeight);
    const rateInitWithDist = R.mergeRight(cloRateInitFields, asyncTotalDistance);

    return R.mergeAll([rateInitWithDist, initValuesWithWeight, totalPickupQuantityToUse]);
  }

  return G.setDefaultFieldsToRateInvoiceAsyncFormik({
    totalWeight,
    totalPickupQuantity,
    formikInitFields: getCloRateInitFields(props),
    services: getCustomerRateInitialServices(props),
    defaultUomFields: G.getDefaultUomFields(branchConfigs),
    // TODO: check rounding for asyncTotalDistance and totalDistance should be the same
    totalDistance: R.mergeRight(asyncTotalDistance, totalDistance),
    currency: G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, branchConfigs),
    mode: G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_TRANSPORTATION_MODE, branchConfigs),
    serviceType: G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_SERVICE_TYPE, branchConfigs),
    equipment: R.or(equipment, G.getConfigValueFromStore(GC.CLO_GENERAL_DEFAULT_EQUIPMENT, branchConfigs)),
  });
};

export const getDivisionGuidByBranchConfigs = (configs, branchInfo) => {
  const telDefaultBranch = G.getConfigValueFromStore(
    GC.TEL_GENERAL_DEFAULT_BRANCH,
    configs,
  );
  const telCreateOnBranch = G.getConfigValueFromStore(
    GC.TEL_GENERAL_CREATE_ON_BRANCH,
    configs,
  );
  const customerBranchGuid = R.prop(GC.FIELD_VALUE, branchInfo);
  const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();
  const customerBranchGuidToUse = G.ifElse(
    G.isNotNilAndNotEmpty(customerBranchGuid),
    customerBranchGuid,
    currentUserBranchGuid,
  );
  const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();
  const defaultTelToGuidMap = {
    [GC.TEL_DEFAULT_BRANCH_SELECTED_BRANCH]: telCreateOnBranch,
    [GC.TEL_DEFAULT_BRANCH_TYPE_CURRENT_BRANCH]: currentBranchGuid,
    [GC.TEL_DEFAULT_BRANCH_TYPE_USER_BRANCH]: currentUserBranchGuid,
    [GC.TEL_DEFAULT_BRANCH_TYPE_CUSTOMER_BRANCH]: customerBranchGuidToUse,
  };

  return R.pathOr(currentBranchGuid, [telDefaultBranch], defaultTelToGuidMap);
};

export const getDivisionGuidToSend = (division, divisionGuid, currentBranchGuid) => R.pathOr(
  R.or(divisionGuid, currentBranchGuid),
  [GC.FIELD_GUID],
  division,
);

export const checkSingleToSingleItemsAndDrop = (props) => {
  const { stops, values, isPickup, setFormDataToStop } = props;
  const condition = G.isAllTrue(
    isPickup,
    R.equals(R.length(R.values(stops)), 2),
    G.isEventTypeDrop(R.path([2, GC.FIELD_EVENT_TYPE], stops)),
  );
  if (condition) {
    const itemIds = R.map(
      R.prop(GC.FIELD_ITEM_INTERNAL_ID),
      values.items,
    );
    const formData = R.compose(
      R.assoc('items', itemIds),
      R.path(['stops', 2, 'formData']),
    )(props);
    setFormDataToStop({ isValid: true, stopOrder: 2, formData });
  }
};

const checkItemDimensions = (item, configDimensions) => {
  const { maxWidth, maxHeight, maxLength, uom } = configDimensions;

  const { dimensionsUOM } = item;

  const dimensionConfigsMap = {
    dimensionsW: maxWidth,
    dimensionsH: maxHeight,
    dimensionsL: maxLength,
  };
  const dimensionLocaleMap = {
    dimensionsW: 'titles:width',
    dimensionsH: 'titles:height',
    dimensionsL: 'titles:length',
  };

  const message = R.compose(
    R.join(', '),
    R.filter(G.isNotNil),
    R.map((name) => {
      const dimension = R.pathOr(null, [name], item);
      const configDimension = R.path([name], dimensionConfigsMap);
      const conditions = [
        G.isNotNilAndNotEmpty(uom),
        G.isNotNilAndNotEmpty(dimension),
        G.isNotNilAndNotEmpty(dimensionsUOM),
        G.isNotNilAndNotEmpty(configDimension),
      ];

      if (G.isAnyFalse(...conditions)) return null;

      const convertedDimension = G.convertDimensionValueByUoms(dimension, dimensionsUOM, uom);

      if (R.gte(configDimension, convertedDimension)) return null;

      return `${G.getWindowLocale(R.prop(name, dimensionLocaleMap))} ${
        G.getWindowLocale('titles:exceeds', 'Exceeds')} ${configDimension} ${G.getUomLocale(uom)}`;
    }),
  )(['dimensionsH', 'dimensionsL', 'dimensionsW']);

  if (R.isEmpty(message)) return null;

  return `${R.pathOr('', [GC.FIELD_ITEM_ID], item)} - ${message}`;
};

export const getValidationWarnings = (store) => {
  const { pageType, ignoreWarnings } = store;

  if (R.or(ignoreWarnings, R.not(isPageCreateDO(pageType)))) return null;

  const { stops, branchConfigs } = store;

  const { pickups } = getDataFromStops(stops);

  const maxWidth = G.getConfigValueFromStore(GC.CLO_ITEM_MAX_WIDTH, branchConfigs);
  const maxHeight = G.getConfigValueFromStore(GC.CLO_ITEM_MAX_HEIGHT, branchConfigs);
  const maxLength = G.getConfigValueFromStore(GC.CLO_ITEM_MAX_LENGTH, branchConfigs);
  const uom = G.getConfigValueFromStore(GC.CLO_ITEM_DIMENSION_UOM, branchConfigs);

  const stopItemsMessages = R.compose(
    R.filter(G.isNotNil),
    R.map((stop) => {
      const itemWarnings = R.compose(
        R.filter(G.isNotNil),
        R.map((item) => checkItemDimensions(item, {
          uom,
          maxWidth,
          maxHeight,
          maxLength,
        })),
        R.pathOr([], ['formData', 'items']),
      )(stop);

      if (R.isEmpty(itemWarnings)) return null;

      const stopName = getStopTitle(stop);

      return `${stopName}: ${itemWarnings}`;
    }),
  )(pickups);

  return stopItemsMessages;
};

export const mapRateInitChargesWithFuelIndexInfo = (rate, shared) => {
  const charges = G.getChargesFromObject(rate);

  if (G.isNilOrEmpty(charges)) return rate;

  const chargesToUse = R.map(
    (item) => {
      const { fuelIndexInfo, sharedAssessorialGuid } = item;

      const id = R.or(G.getGuidFromObject(item), G.genShortId());

      if (R.or(G.isNilOrEmpty(fuelIndexInfo), G.isNilOrEmpty(sharedAssessorialGuid))) {
        return {
          ...item,
          [GC.FIELD_ID]: id,
          fuelIndexInfo: null,
        };
      }

      const sharedAss = R.find(R.propEq(sharedAssessorialGuid, GC.FIELD_GUID), shared);

      if (G.isNilOrEmpty(sharedAss)) {
        return {
          ...item,
          [GC.FIELD_ID]: id,
          fuelIndexInfo: null,
        };
      }

      const { rate, fuelIndexInfo: fuelIndexInfoToUse } = getRateFromSharedAccessAndFuelPrices(sharedAss);

      return {
        ...item,
        rate,
        [GC.FIELD_ID]: id,
        fuelIndexInfo: fuelIndexInfoToUse,
      };
    },
    charges,
  );

  return R.assoc(GC.FIELD_CHARGES, chargesToUse, rate);
};

export const getRateWithChargesFromData = (
  rate,
  branchConfigs,
  isValid,
  shared,
  equipmentServices,
) => {
  if (G.isNilOrEmpty(rate)) return null;

  const currency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, branchConfigs);
  const mode = G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_TRANSPORTATION_MODE, branchConfigs);
  const defaultEquipment = G.getConfigValueFromStore(GC.CLO_GENERAL_DEFAULT_EQUIPMENT, branchConfigs);

  const equipment = R.pathOr(
    defaultEquipment,
    [GC.FIELD_LOAD_EQUIPMENT, GC.FIELD_DROPDOWN_OPTION_GUID],
    equipmentServices,
  );

  const services = R.map(
    (item) => R.prop(GC.FIELD_DROPDOWN_OPTION_GUID, item),
    R.propOr([], GC.FIELD_LOAD_SERVICES, equipmentServices),
  );

  const init = {
    services,
    equipment,
    [GC.FIELD_MODE]: mode,
    isValid: R.or(isValid, false),
    [GC.FIELD_CURRENCY]: currency,
  };

  return R.mergeRight(init, mapRateInitChargesWithFuelIndexInfo(rate, shared));
};

export const prependCurrentBranchToBranchList = (list, currentBranch) => {
  if (G.isNilOrEmpty(currentBranch)) return list;

  return R.prepend(
    R.compose(
      R.assoc(
        GC.FIELD_ACTIVE,
        R.propOr(true, GC.FIELD_ACTIVE, R.find(R.propEq(G.getGuidFromObject(currentBranch), GC.FIELD_GUID), list)),
      ),
      R.assoc(GC.FIELD_NAME, R.prop(GC.FIELD_BRANCH_NAME, currentBranch)),
    )(currentBranch),
  )(list);
};

export const getLeftSectionWidth = (leftActiveTab, aiEnabled) => G.ifElse(
  R.equals(leftActiveTab, TAB_NAME_PRICING),
  G.ifElse(aiEnabled, 830, 755),
  662,
);

export const getRightSectionWidth = (leftActiveTab, rightActiveTab) => {
  const leftSectionWidth = getLeftSectionWidth(leftActiveTab);

  const subtractWidth = R.add(150, leftSectionWidth);

  if (R.equals(rightActiveTab, TAB_NAME_RATE_CONFIRMATION)) {
    return `calc(100vw - ${subtractWidth}px)`;
  }

  return 662;
};

export const getPrimaryReference = (branchConfigs, branchRefTypes = {}) => {
  const primaryReferenceGuid = G.getConfigValueFromStore(GC.CLO_PRIMARY_REFERENCE_TYPE, branchConfigs);

  const primaryReferenceName = R.compose(
    R.pathOr('', [GC.FIELD_NAME]),
    R.find(R.propEq(primaryReferenceGuid, GC.FIELD_GUID)),
    R.pathOr([], [GC.REF_SCOPE_NAME_CLO]),
  )(branchRefTypes);

  return {
    [GC.FIELD_VALUE]: primaryReferenceGuid,
    [GC.FIELD_LABEL]: primaryReferenceName,
  };
};

// SAGA
export const getLocationTemplateGuidsFromOrderTemplate = ({ events }, guids) => R.compose(
  R.without(guids),
  R.filter(G.isNotNilAndNotEmpty),
  R.map(R.pathOr(null, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_GUID])),
)(events);
